import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
    Directive,
    AfterViewInit,
    Renderer2,
    ElementRef,
    OnDestroy,
    Input,
    Output,
    EventEmitter,
    Inject,
    PLATFORM_ID,
    NgZone,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, fromEvent } from 'rxjs';

export interface IScrollToEvent {
    elemPosition: number;
    smooth: boolean;
}

@UntilDestroy()
@Directive({
    selector: '[dragScroll]',
    standalone: true,
})
export class DragScrollDirective implements AfterViewInit, OnDestroy {
    @Input() scrollToElementEvent: BehaviorSubject<IScrollToEvent> = new BehaviorSubject<IScrollToEvent>(null);
    @Input() disableScrollEvent: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

    @Input() scrollElementLeft: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    @Input() scrollElementRight: BehaviorSubject<number> = new BehaviorSubject<number>(null);

    @Input() scrollElementLeftAuto: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    @Input() scrollElementRightAuto: BehaviorSubject<number> = new BehaviorSubject<number>(null);

    @Output() reachesRightBound: EventEmitter<boolean> = new EventEmitter<boolean>(false);
    @Output() reachesLeftBound: EventEmitter<boolean> = new EventEmitter<boolean>(false);

    private leftElementIndex: number = null;
    private rightElementIndex: number = null;
    private previousPosition: number;
    private moving = false;

    private observer: MutationObserver = null;

    private get children(): Array<HTMLElement> {
        return Array.from(this.elementRef.nativeElement.children);
    }

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: Object,
        private zone: NgZone,
    ) { }

    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => {
            this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'flex');
            this.renderer.setStyle(this.elementRef.nativeElement, 'overflow-x', 'auto');
            this.renderer.setStyle(this.elementRef.nativeElement, 'overflow-y', 'hidden');
            this.renderer.setStyle(this.elementRef.nativeElement, 'user-select', 'none');
            this.renderer.setStyle(
                this.elementRef.nativeElement,
                '-ms-overflow-style',
                'none',
            ); /* Internet Explorer 10+ */
            this.renderer.setStyle(this.elementRef.nativeElement, 'scrollbar-width', 'none'); /* Firefox */
            this.renderer.addClass(this.elementRef.nativeElement, 'hidden-scroll');

            fromEvent(this.document, 'mouseup')
                .pipe(untilDestroyed(this))
                .subscribe(() => {
                    this.moving = false;

                    setTimeout(() => {
                        this.clickSwitcher(true);
                    }, 100);
                });

            fromEvent(this.elementRef.nativeElement, 'mousedown')
                .pipe(untilDestroyed(this))
                .subscribe((event: MouseEvent) => {
                    event.stopPropagation();
                    this.moving = true;
                    this.previousPosition = event.screenX;
                });

            fromEvent(this.document, 'mouseleave')
                .pipe(untilDestroyed(this))
                .subscribe(() => {
                    this.moving = false;

                    setTimeout(() => {
                        this.clickSwitcher(true);
                    }, 100);
                });

            fromEvent(this.document, 'mousemove')
                .pipe(untilDestroyed(this))
                .subscribe((event: MouseEvent) => {
                    if (this.moving && this.previousPosition !== event.screenX) {
                        this.clickSwitcher(false);
                        this.scrollByDistance(this.previousPosition - event.screenX);
                        this.previousPosition = event.screenX;
                    }
                });

            fromEvent(this.elementRef.nativeElement, 'scroll')
                .pipe(untilDestroyed(this))
                .subscribe(() => {
                    this.calcUtmostPositions();
                });

            // Fixed dragging images
            fromEvent(this.elementRef.nativeElement, 'dragstart')
                .pipe(untilDestroyed(this))
                .subscribe((event: MouseEvent) => {
                    event.preventDefault();
                });

            this.scrollToElementEvent.pipe(untilDestroyed(this)).subscribe((data: IScrollToEvent) => {
                if (data) {
                    this.scrollToElement(data.elemPosition, data.smooth);
                }
            });

            this.scrollElementLeft.pipe(untilDestroyed(this)).subscribe((data: number) => {
                if (data) {
                    this.scrollToElement(this.leftElementIndex - data, true);
                }
            });

            this.scrollElementRight.pipe(untilDestroyed(this)).subscribe((data: number) => {
                if (data) {
                    this.scrollToElement(this.rightElementIndex + data, true);
                }
            });

            this.scrollElementRightAuto.pipe(untilDestroyed(this)).subscribe((data: number) => {
                if (data) {
                    const elements = [...this.children];
                    const elem: HTMLElement = elements[this.rightElementIndex];
                    if (elem?.scrollIntoView) {
                        elem.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
                    }
                }
            });

            this.scrollElementLeftAuto.pipe(untilDestroyed(this)).subscribe((data: number) => {
                if (data) {
                    const elements = [...this.children];
                    const elem: HTMLElement = elements[this.leftElementIndex];
                    if (elem?.scrollIntoView) {
                        elem.scrollIntoView({ inline: 'end', block: 'nearest', behavior: 'smooth' });
                    }
                }
            });

            this.disableScrollEvent.pipe(untilDestroyed(this)).subscribe((data: boolean) => {
                if (data) {
                    this.renderer.setStyle(this.elementRef.nativeElement, 'overflow-x', 'hidden');
                }
            });

            this.observer = new MutationObserver(() => {
                setTimeout(() => this.calcUtmostPositions());
            });

            this.observer.observe(this.elementRef.nativeElement, { childList: true });
            setTimeout(() => this.calcUtmostPositions(true));
        });
    }

    ngOnDestroy(): void {
        this.observer.disconnect();
    }

    private clickSwitcher(state: boolean) {
        [...this.children].forEach((elem: HTMLElement) => {
            if (elem) {
                this.renderer.setStyle(elem, 'pointer-events', state ? 'auto' : 'none');
            }
        });
    }

    private scrollToElement(elemPosition: number, smooth: boolean) {
        setTimeout(() => {
            const elements = [...this.children];
            let resPosition = elemPosition;

            if (elemPosition > elements.length - 1) {
                resPosition = elements.length - 1;
            }
            if (elemPosition < 0) {
                resPosition = 0;
            }

            const elem: HTMLElement = elements[resPosition];
            if (elem?.scrollIntoView) {
                elem.scrollIntoView({ inline: 'center', block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
            }
        }, 0);
    }

    private scrollByDistance(distance: number) {
        this.elementRef.nativeElement.scrollTo(this.elementRef.nativeElement?.scrollLeft + distance, 0);
    }

    private calcUtmostPositions(init = false) {
        const elements = [...this.children];

        let left: number = null;
        let right: number = null;
        let notVisibleAll: boolean = true;

        elements.forEach((elem: HTMLElement, index: number) => {
            const checkVisibility = this.checkVisible(elem);
            const isVisible = checkVisibility.isVisible;

            if (notVisibleAll && !checkVisibility.zeroSize) {
                notVisibleAll = false;
            }

            if (isVisible && !left && left !== 0) {
                left = index;
            }
            if (isVisible && right < index) {
                right = index;
            }
        });

        if (notVisibleAll && init) {
            setTimeout(() => this.calcUtmostPositions(), 500);
        }

        if (left !== null || right !== null) {
            if (!left) {
                left = 0;
            }
            if (!right) {
                right = 0;
            }
        }

        if (left === 0) {
            this.reachesLeftBound.next(true);
        } else {
            this.reachesLeftBound.next(false);
        }

        if (right === elements.length - 1) {
            this.reachesRightBound.next(true);
        } else {
            this.reachesRightBound.next(false);
        }

        this.leftElementIndex = left;
        this.rightElementIndex = right;
    }

    private checkVisible(elem: HTMLElement) {
        if (isPlatformServer(this.platformId)) {
            return;
        }
        const childRect = elem.getBoundingClientRect();
        const rect = this.elementRef.nativeElement.getBoundingClientRect();

        const leftPadding = +this.document.defaultView
            .getComputedStyle(elem, '')
            .getPropertyValue('padding-left')
            .replace('px', '');
        const rightPadding = +this.document.defaultView
            .getComputedStyle(elem, '')
            .getPropertyValue('padding-right')
            .replace('px', '');

        return {
            zeroSize: childRect.width === 0 && childRect.height === 0,
            isVisible: Math.round(childRect.left) + leftPadding >= rect.left && Math.round(childRect.right - rightPadding) <= rect.right,
        }
    }
}